import { useState, useMemo } from "react";
import { first } from "lodash";
import {
  createTabsData,
  getFilteredTags,
  transformTagsToTreeData,
} from "../utils/tagsDataUtils";
import { useTagsHierarchyViewer } from "../context";

/**
 * Custom hook for managing tags tabs functionality
 * @param {Object} toggledNodes - Object tracking which nodes are expanded
 * @returns {Object} Object containing tabs data, selected tab, handlers, and tree data
 */
export const useTagsTabs = (toggledNodes = {}) => {
  const { tags, tagTypes } = useTagsHierarchyViewer();

  // Group tags by type and create tabs data
  const tabsData = useMemo(() => {
    return createTabsData(tags, tagTypes);
  }, [tags, tagTypes]);

  const [selectedTabType, setSelectedTabType] = useState(
    first(tabsData)?.id || null
  );

  const tabsList = useMemo(() => {
    return tabsData.map((tab) => ({
      label: `${tab.name} (${tab.count})`,
      path: `#${tab.id}`,
      id: tab.id,
      onClick: () => setSelectedTabType(tab.id),
      className: selectedTabType === tab.id ? "active" : "",
      "data-testid": `tab-${tab.id}`,
    }));
  }, [tabsData, selectedTabType]);

  // Get filtered tags for selected tab
  const filteredTags = useMemo(() => {
    return getFilteredTags(tabsData, selectedTabType);
  }, [tabsData, selectedTabType]);

  // Transform filtered tags data into TreeView format
  const treeData = useMemo(() => {
    return transformTagsToTreeData(filteredTags, toggledNodes);
  }, [filteredTags, toggledNodes]);

  return {
    tabsData,
    tabsList,
    selectedTabType,
    treeData,
  };
};
